import { RESPONSE_CHANNEL_TYPE, SourceType } from '@config/index';
import {
  BasePageModel,
  basePageToLocal,
  ActionType,
  ActionChannel,
  IntentStartAction,
  IntentAssignment,
  FramelyAction,
  TrainingPhraseConfig,
  PromptInfo,
  promptInfoToLocal,
  promptInfoToServer,
  Reference,
  referenceNodeToLocal,
  referenceNodeToServer,
  InputParameter,
  inputParameterToLocal,
  inputParameterToServer,
  basePageToServer
} from '@interface/index';
import { trainingPhraseToServer, trainingPhraseToLocal } from './training-phrase';
import { sources } from 'webpack';

export enum ValueRecommendationSourceType {
  codeExpression = 'code_expression',
  intent = 'intent'
}

export type ValueSuggestionData = BasePageModel & {
  vrSourceType: ValueRecommendationSourceType;
  sourceType: SourceType;
  source: string;
  sourceComment?: string;
  variable: Reference;
  parameters: InputParameter[];
  sourceIntent?: IntentStartAction;
  channels: ActionChannel[];
  soft: boolean;
  entryConfirm?: boolean;
  singlePrompts: PromptInfo[];
  singleAffirmatives: TrainingPhraseConfig[];
  singleNegatives: TrainingPhraseConfig[];
  zeroPrompts: PromptInfo[];
  masterSelf?: ValueSuggestionData;
};

export function valueSuggestionToLocal(vs: any): ValueSuggestionData {

  const res: any = {
    ...basePageToLocal(vs),
    vrSourceType: vs.vr_source_type || ValueRecommendationSourceType.codeExpression,
    sourceType: vs.source_type ||
      (vs.source ? SourceType.CODE_EXPRESSION : SourceType.REGULAR),
    source: vs.source,
    sourceComment: vs.source_comment,
    variable: (vs.variable || []).map(referenceNodeToLocal),
    parameters: (vs.parameters || []).map(inputParameterToLocal),
    sourceIntent: vs.source_intent && {
      id: vs.source_intent.id,
      type: 'intent_start_action',
      intent: vs.source_intent.intent && {
        id: vs.source_intent.intent.id,
        name: vs.source_intent.intent.name,
        type: vs.source_intent.intent.type
      },
      assignments: (vs.source_intent.assignments || []).map((asm: any) => ({
        slot: asm.slot && asm.slot.map(referenceNodeToLocal),
        codeExpression: asm.code_expression,
        codeComment: asm.code_comment
      }))
    },
    singlePrompts: vs.prompts && vs.prompts.map(promptInfoToLocal),
    zeroPrompts: vs.zero_prompts && vs.zero_prompts.map(promptInfoToLocal),
    soft: vs.soft,
    singleAffirmatives: vs.single_affirmatives &&
      vs.single_affirmatives.map(trainingPhraseToLocal),
    singleNegatives: vs.single_negatives &&
      vs.single_negatives.map(trainingPhraseToLocal),
    masterSelf: vs.master_self && valueSuggestionToLocal(vs.master_self)
  };

  const channels: ActionChannel[] = [];

  for (const resChannel of (vs.channels || [])) {

    const actions: FramelyAction[] = [];

    (resChannel.actions || [])
      .forEach((data: any) => {

        if (data && data[data.type]) {
          const resItem = data[data.type];
          actions.push({
            id: resItem.id,
            type: 'list_action',
            sourceType: resItem.source_type,
            source: resItem.source,
            variable: (data.variable || []).map(referenceNodeToLocal),
            parameters: (data.parameters || []).map(inputParameterToLocal),
            itemSize: resItem.item_size,
            header: resItem.header,
            footer: resItem.footer,
            body: resItem.body,
            templateId: resItem.template_id,
            delimiterField: resItem.delimiter_field,
            delimiterEnd: resItem.delimiter_end
          });
        }
      });

    if (!!channels.find((c: ActionChannel) => c.tag === resChannel.tag)) {
      continue;
    }

    channels.push({
      actions,
      tag: resChannel.tag
    });
  }
  res.channels = channels;
  return res;
}

export function valueSuggestionToServer(vs: ValueSuggestionData) {
  const res: any = {
    ...basePageToServer(vs),
    id: vs.id,
    frame_id: vs.frameId,
    owner_id: vs.ownerId,
    owner_type: vs.ownerType,
    vr_source_type: vs.vrSourceType || ValueRecommendationSourceType.codeExpression,
    source_type: vs.sourceType ||
      (vs.source ? SourceType.CODE_EXPRESSION : SourceType.REGULAR),
    source: vs.source,
    source_comment: vs.sourceComment,
    variable: (vs.variable || []).map(referenceNodeToServer),
    parameters: (vs.parameters || []).map(inputParameterToServer),
    source_intent: vs.sourceIntent && {
      intent: vs.sourceIntent.intent && {
        id: vs.sourceIntent.intent.id,
        name: vs.sourceIntent.intent.name,
        type: vs.sourceIntent.intent.type
      },
      assignments: (vs.sourceIntent.assignments || []).map((asm: IntentAssignment) => ({
        slot: asm.slot && asm.slot.map(referenceNodeToServer),
        code_expression: asm.codeExpression,
        code_comment: asm.codeComment
      }))
    },
    channels: (vs.channels || []).map((channel: ActionChannel) => ({
      tag: channel.tag,
      actions: (channel.actions || []).map((action: any) => {
        return {
          type: ActionType.LIST,
          [ActionType.LIST]: {
            id: action.id,
            item_size: action.itemSize,
            header: action.header,
            footer: action.footer,
            body: action.body,
            template_id: action.templateId,
            data_query_id: action.dataQueryId,
            delimiter_field: action.delimiterField,
            delimiter_end: action.delimiterEnd
          }
        };
      })
    })),
    soft: vs.soft,
    prompts: vs.singlePrompts && vs.singlePrompts.map(promptInfoToServer),
    zero_prompts: vs.zeroPrompts && vs.zeroPrompts.map(promptInfoToServer),
    single_affirmatives: vs.singleAffirmatives && vs.singleAffirmatives.map(trainingPhraseToServer),
    single_negatives: vs.singleNegatives && vs.singleNegatives.map(trainingPhraseToServer),
    to_be_deleted: vs.toBeDeleted
  };

  if (!res.id || !res.id.length) {
    delete res.id;
  }

  return res;
}

export const DefaultValueSuggestionChannels = [
  {
    tag: RESPONSE_CHANNEL_TYPE.RESTFUL,
    actions: [
      {
        type: 'list_action',
        itemSize: 5,
        delimiterField: '\\n',
        header: '',
        footer: '',
        body: '',
        dataQueryId: undefined
      }
    ]
  }];
